import React from 'react';
import {
  ATMDivider,
  ATMForm,
  ATMGrid,
  ATMHeader,
  ATMSegment,
} from 'shared-it-appmod-ui';
import FieldReadOnlyCommment from 'src/components/atoms/field/field-readonly-comment/field-readonly-comment.component';
import FieldReadOnly from 'src/components/atoms/field/field-readonly/field-readonly.component';
import FlagReadOnly from 'src/components/atoms/flag-readonly/flag-readonly.component';
import FileManager from 'src/components/molecules/file-manager/file-manager.component';
import Lang from 'src/libraries/language';
import { IRequest } from 'src/models/request.model';
import style from '../flight-request-drone.module.scss';

type IProps = {
  data: IRequest;
};

const FlightRequestDroneContractorReviewComponent: React.FC<IProps> = ({
  data: { droneFlightContractor },
}) => {
  return (
    <ATMSegment color="teal" className={style.wrapper}>
      <ATMHeader>{Lang.TTL_DRONE_FLIGHT_CONTRACTOR}</ATMHeader>

      <ATMForm>
        <ATMGrid>
          <ATMGrid.Row columns={4}>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_FLIGHT_TYPE}>
                {droneFlightContractor.flightType}
              </FieldReadOnly>
            </ATMGrid.Column>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_FLIGHT_OPERATOR}>
                {droneFlightContractor.flightOperator}
              </FieldReadOnly>
            </ATMGrid.Column>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_PILOT_IN_COMMAND}>
                {droneFlightContractor.pilotName}
              </FieldReadOnly>
            </ATMGrid.Column>
          </ATMGrid.Row>
          <ATMGrid.Row>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_ADDRESS_CROSS_STREET}>
                {droneFlightContractor.address}
              </FieldReadOnly>
            </ATMGrid.Column>
          </ATMGrid.Row>
          {droneFlightContractor.purpose && (
            <ATMGrid.Row>
              <ATMGrid.Column width="16">
                <FieldReadOnly
                  label={Lang.LBL_PURPOSE}
                  className={style.wrapText}
                >
                  {droneFlightContractor.purpose}
                </FieldReadOnly>
              </ATMGrid.Column>
            </ATMGrid.Row>
          )}
          <ATMGrid.Row columns="equal">
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_PROJECT}>
                {droneFlightContractor.project}
              </FieldReadOnly>
            </ATMGrid.Column>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_TOWN_CITY}>
                {droneFlightContractor.destination}
              </FieldReadOnly>
            </ATMGrid.Column>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_FACILITY_ASSET_NUMBER}>
                {droneFlightContractor.asset}
              </FieldReadOnly>
            </ATMGrid.Column>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_PIPELINE_NUMBER}>
                {droneFlightContractor.circuit}
              </FieldReadOnly>
            </ATMGrid.Column>
          </ATMGrid.Row>
        </ATMGrid>

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_CONTACT_INFORMATION}
        </ATMHeader>

        <ATMGrid>
          <ATMGrid.Row columns={4}>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_CONTRACTOR_NAME}>
                {droneFlightContractor.primeName}
              </FieldReadOnly>
            </ATMGrid.Column>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_CONTRACTOR_EMAIL}>
                {droneFlightContractor.primeEmail}
              </FieldReadOnly>
            </ATMGrid.Column>
          </ATMGrid.Row>
        </ATMGrid>

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_OTHER_INFORMATION}
        </ATMHeader>

        <ATMGrid columns={1}>
          <ATMGrid.Column>
            <FlagReadOnly
              value={droneFlightContractor.nightFlag}
              label={Lang.LBL_NIGHT_FLIGHT}
            />
          </ATMGrid.Column>

          {droneFlightContractor?.isOldFlight ? (
            <>
              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightContractor.sensitiveFlag}
                  label={Lang.LBL_SENSITIVE_CUSTOMER}
                />

                {droneFlightContractor.sensitiveFlag && (
                  <FileManager
                    files={droneFlightContractor?.sensitiveFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>
              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightContractor.culturalFlag}
                  label={Lang.LBL_CULTURAL_RESTRICTION}
                />

                {droneFlightContractor.culturalFlag && (
                  <FileManager
                    files={droneFlightContractor?.culturalFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>
              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightContractor.landFlag}
                  label={Lang.LBL_LAND_MANAGEMENT_APPROVAL_REQUIREMENT}
                />

                {droneFlightContractor.landFlag && (
                  <FileManager
                    files={droneFlightContractor?.landFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>
              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightContractor.safetyFlag}
                  label={Lang.LBL_PUBLIC_SAFETY_CONCERNS}
                />

                {droneFlightContractor.safetyFlag && (
                  <FileManager
                    files={droneFlightContractor?.safetyFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>

              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightContractor.dnadeFlag}
                  label={Lang.LBL_NOTIFICATION_SENT}
                />
              </ATMGrid.Column>
            </>
          ) : (
            <>
              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightContractor.airspaceFlag}
                  label={Lang.LBL_DRONE_OTHER_QUESTION_1}
                />

                {droneFlightContractor.airspaceFlag && (
                  <FileManager
                    files={droneFlightContractor?.airspaceFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>

              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightContractor.safetyFlag}
                  label={Lang.LBL_DRONE_OTHER_QUESTION_2}
                  no={false}
                  na
                />

                {droneFlightContractor.safetyFlag && (
                  <FileManager
                    files={droneFlightContractor?.safetyFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>

              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightContractor.sensitiveFlag}
                  label={Lang.LBL_DRONE_OTHER_QUESTION_3}
                  no={false}
                  na
                />
              </ATMGrid.Column>

              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightContractor.landFlag}
                  label={Lang.LBL_DRONE_OTHER_QUESTION_4}
                />

                {droneFlightContractor.landFlag && (
                  <FileManager
                    files={droneFlightContractor?.landFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>

              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightContractor.culturalFlag}
                  label={Lang.LBL_DRONE_OTHER_QUESTION_5}
                />

                {droneFlightContractor.culturalFlag && (
                  <FileManager
                    files={droneFlightContractor?.culturalFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>
            </>
          )}
        </ATMGrid>

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_BRIEF_JOB_DESCRIPTION}
        </ATMHeader>
        <p>{Lang.MSG_BRIEF_DESCRIPTION}</p>

        <ATMGrid>
          <ATMGrid.Column>
            <FieldReadOnlyCommment label={Lang.LBL_DESCRIPTION_COMMENT}>
              {droneFlightContractor.jobDesc}
            </FieldReadOnlyCommment>
          </ATMGrid.Column>
        </ATMGrid>

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_FLIGHT_AREA}
        </ATMHeader>
        <ATMForm.Field>
          <FileManager
            files={droneFlightContractor.flightAreaFiles}
            editable={false}
            inline
          />
        </ATMForm.Field>
        <ATMDivider />

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_OTHER_SPECIAL_COMMENTS}
        </ATMHeader>

        <ATMGrid>
          <ATMGrid.Column>
            <FieldReadOnlyCommment>
              {droneFlightContractor.comments}
            </FieldReadOnlyCommment>
          </ATMGrid.Column>
        </ATMGrid>
      </ATMForm>
    </ATMSegment>
  );
};

export default FlightRequestDroneContractorReviewComponent;
